'use client';

import {
	useState,
	useEffect,
	useRef,
	isValidElement,
	type ReactElement,
	cloneElement,
	type ReactNode,
	type KeyboardEvent,
} from 'react';
import styles from './tooltip.module.scss';
import MarkdownParser from 'components/rich-text/markdown-parser';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import InfoSVG from 'assets/icons/info.svg';

const ToolTip = ({
	id,
	description,
	jsxDescription,
	onOpenOrClose,
	children,
	alwaysOpen = false,
	clickToOpen = false,
}: PropsWithJSX | PropsWithPlainDescription) => {
	const [isVisible, setIsVisible] = useState(false);
	const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
	const contentRef = useRef(null);
	const didMountRef = useRef(false);
	const childRef = useRef<HTMLElement | null>(null);
	const timeout = useRef(null); // Persisting timeout across renders

	// TODO: Convert to useEffectEvent once we upgrade to React@^19.2.
	const onOpenOrCloseRef = useRef<((isOpen: boolean) => void) | null>(
		onOpenOrClose ?? null,
	);
	useEffect(() => {
		onOpenOrCloseRef.current = onOpenOrClose;
	}, [onOpenOrClose]);

	useEffect(() => {
		// Skip the first render to avoid calling onOpenOrClose on mount.
		if (!didMountRef.current) {
			didMountRef.current = true;
			return;
		}

		// Always focus the child element when closing the tooltip for accessibility.
		// Handles cases where the tooltip has inputs that can grab focus on interaction.
		if (!isVisible) {
			childRef.current?.focus();
		}

		onOpenOrCloseRef.current?.(isVisible);
	}, [isVisible]);

	useEffect(() => {
		if (!alwaysOpen) {
			return;
		}

		onOpenOrCloseRef.current?.(true);
	}, [alwaysOpen]);

	const clearTooltipTimeout = () => {
		clearTimeout(timeout.current);
	};

	const handleOpenPopUp = () => {
		if (clickToOpen) {
			return;
		}

		clearTooltipTimeout();
		setIsVisible(true);
	};

	const handleClosePopUpOnTimeOut = () => {
		if (clickToOpen) {
			return;
		}

		timeout.current = setTimeout(() => {
			setIsVisible(false);
		}, 1000);
	};

	const handleClick = (e) => {
		e.preventDefault();
		setIsVisible(!isVisible);
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Escape' && isVisible) {
			setIsVisible(false);
		}
	};

	const buildAccessibleButton = () => {
		const newProps = {
			onClick: handleClick,
			onKeyDown: handleKeyDown,
			role: 'button',
			tabIndex: 0,
			'aria-describedby': `tooltip-${id}`,
			'aria-haspopup': true,
			'aria-expanded': isVisible,
		};

		// Clone and add accessibility attributes so that the child itself is
		// treated as a focusable button, regardless of what type of HTMLElement
		// is passed in.
		if (isValidElement(children)) {
			return cloneElement(children as ReactElement<any>, newProps);
		}

		// Fallback to a wrapper class to add all these attributes to if the
		// child is not a valid React element. Not ideal because it'll grab focus,
		// but good to handle this case.
		return (
			<span className={styles.button} {...newProps}>
				{children ?? <InfoIcon />}
			</span>
		);
	};

	return (
		<div
			ref={setContainerEl}
			onMouseEnter={handleOpenPopUp}
			onMouseLeave={handleClosePopUpOnTimeOut}
		>
			{containerEl ? (
				<Popover
					containerClassName={styles.tooltip}
					parentElement={containerEl}
					padding={4}
					isOpen={isVisible || alwaysOpen}
					boundaryElement={document.querySelector('body')}
					ref={childRef}
					onClickOutside={() => setIsVisible(false)}
					positions={['top', 'bottom', 'left', 'right']}
					content={({ position, childRect, popoverRect }) => (
						<ArrowContainer
							position={position}
							childRect={childRect}
							popoverRect={popoverRect}
							arrowColor={'var(--color-site-background)'}
							arrowSize={10}
						>
							<span ref={contentRef} id={`tooltip-${id}`}>
								<div className={styles.content}>
									{jsxDescription || (
										<div className={styles.descriptionWrapper}>
											<MarkdownParser>{description}</MarkdownParser>
										</div>
									)}
								</div>
								<noscript>
									<span className={`${styles.tooltip} ${styles.isVisible}`}>
										<MarkdownParser>{description}</MarkdownParser>
									</span>
								</noscript>
							</span>
						</ArrowContainer>
					)}
				>
					{buildAccessibleButton()}
				</Popover>
			) : (
				<span className={styles.button}>{children ?? <InfoIcon />}</span>
			)}
		</div>
	);
};

function InfoIcon() {
	return <InfoSVG className={styles.icon} />;
}

export interface PropsWithJSX extends Props {
	jsxDescription: ReactNode;
	description?: never;
}

export interface PropsWithPlainDescription extends Props {
	jsxDescription?: never;
	description: string;
}

interface Props {
	id: string;
	alwaysOpen?: boolean;
	onOpenOrClose?: (isOpen: boolean) => void;
	children?: ReactNode;
	clickToOpen?: boolean;
}

export default ToolTip;
